import { getLogger } from '@logtape/logtape';
import { createElement } from 'react';

import { CollectionSuccessNotification } from '@/components/collection-success-notification.tsx';

import { revalidateAppointmentsRangeCache } from '@/hooks/use-appointments-for-date-range.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { NotificationType } from '@/models/Notification.ts';
import { CollectionStep } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { getCorrectInsuranceForForms, updateAppointmentSeries } from '@/services/appointmentService.ts';
import { showErrorNotification, showLoadingNotification, updateNotification } from '@/services/notificationService.ts';

import { selectCurrentForm, useBookingStore } from '@/stores/bookingStore.ts';

import {
    buildUpdateCollectionDto,
    createAppointmentDtoFromForm,
    resolveDocCirrusPvsUserId,
} from '@/utils/appointmentUtils.ts';
import { resolvePatientUserId, resolveUserId } from '@/utils/patientUtils.ts';

const logger = getLogger(['hook', 'update-appointment-collection']);

interface UpdateAppointmentCollectionHook {
    updateCollection: (collectionId: string, onClose?: () => void, sendNotification?: boolean) => Promise<void>;
}

export const useUpdateAppointmentCollection = (): UpdateAppointmentCollectionHook => {
    const { selected_customer, selected_location } = useConfig();
    const config = useBookingStore((s) => s.collection_configuration);
    const currentForm = useBookingStore(selectCurrentForm);
    const currentPatient = useBookingStore((s) => s.current_patient);
    const { data: enrichedPractitionerData } = useEnrichedPractitionerDataForSpecificInstance(
        currentPatient?.instance_id ?? ''
    );

    const updateCollection: UpdateAppointmentCollectionHook['updateCollection'] = async (
        collectionId,
        onClose,
        sendNotification
    ) => {
        const { collection_occurrences: freshOccurrences } = useBookingStore.getState();

        if (!config || !currentForm || !currentPatient || !enrichedPractitionerData) {
            showErrorNotification('Fehlende Daten für die Aktualisierung der Terminserie');
            return;
        }

        if (freshOccurrences.length === 0) {
            showErrorNotification('Keine Termine für die Serie vorhanden');
            return;
        }

        const userId = resolvePatientUserId(currentPatient);
        if (!userId) {
            showErrorNotification('Benutzer-ID ist ungültig');
            return;
        }

        useBookingStore.setState({ is_booking_loading: true });

        const instanceId = currentPatient.instance_id;
        let notificationId = '';

        try {
            // DocCirrus requires insurance verification to resolve the PVS-specific user ID.
            // Other PVS providers (DataCur) can use the patient's user ID directly.
            const formWithId = { ...currentForm, form_id: currentForm.id };
            let pvsUserId: string;
            let eternoUserId: string | undefined;
            if (enrichedPractitionerData.pvs_provider === PVSProvider.DOC_CIRRUS) {
                const result = await getCorrectInsuranceForForms(
                    [formWithId],
                    selected_customer.customer_id,
                    instanceId,
                    userId,
                    false
                );
                const resolved = resolveDocCirrusPvsUserId(result);
                if (!resolved.success) {
                    showErrorNotification(resolved.errorMessage);
                    useBookingStore.setState({ is_booking_loading: false });
                    return;
                }
                pvsUserId = resolved.userId;
                eternoUserId = resolved.eternoUserId;
            } else {
                pvsUserId = currentPatient.pvs_user_id;
                eternoUserId = currentPatient.eterno_user_id;
            }

            const baseDto = createAppointmentDtoFromForm(
                { ...formWithId, pvs_user_id: pvsUserId, eterno_user_id: eternoUserId },
                enrichedPractitionerData,
                selected_location,
                currentPatient
            );
            if (!baseDto) {
                showErrorNotification('Fehler beim Erstellen der Termindaten');
                useBookingStore.setState({ is_booking_loading: false });
                return;
            }

            notificationId = showLoadingNotification(
                `Aktualisiere ${freshOccurrences.length} Termine...`,
                'Terminserie'
            );

            const dto = buildUpdateCollectionDto(
                baseDto,
                config,
                freshOccurrences,
                instanceId,
                collectionId,
                sendNotification
            );

            const resolvedUser = resolveUserId(eternoUserId, instanceId, pvsUserId);
            const { appointments } = await updateAppointmentSeries(selected_customer.customer_id, resolvedUser.id, dto);

            updateNotification(NotificationType.SUCCESS, {
                id: notificationId,
                message: createElement(CollectionSuccessNotification, { appointments }),
                title: 'Terminserie erfolgreich aktualisiert',
            });
            revalidateAppointmentsRangeCache();
            useBookingStore.setState({
                isSeriesMode: false,
                editingCollectionId: undefined,
                collectionStep: CollectionStep.CONFIG,
                collection_occurrences: [],
                collection_configuration: undefined,
                collectionPastAppointments: [],
                is_booking_loading: false,
            });
            if (onClose) onClose();
        } catch (error) {
            logger.error('Collection update failed', { error: error });
            if (notificationId) {
                updateNotification(NotificationType.ERROR, {
                    id: notificationId,
                    message: 'Fehler beim Aktualisieren der Terminserie. Bitte versuchen Sie es erneut.',
                    title: 'Fehler',
                });
            } else {
                showErrorNotification('Fehler beim Aktualisieren der Terminserie. Bitte versuchen Sie es erneut.');
            }
            useBookingStore.setState({ is_booking_loading: false });
        }
    };

    return { updateCollection };
};
